import {
    Box,
    Grid,
    InputLabel,
    Typography,
    darken,
    lighten,
    styled,
} from '@mui/material';
import React from 'react';
import { capitalizeName, camelCaseToTitleCase } from '../../../../shared/utils';
import { CustomTextField } from '../../../../layout/styledcomponents/CustomTextField';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import { canEditReservationFields } from './reservationEdit.utils';

const StyledInputLabel = styled(InputLabel)({
    fontWeight: 'bold',
    fontSize: '14px',
});

type LabelRowProps = {
    label: React.ReactNode;
    children: React.ReactNode;
    required?: boolean;
    labelXs?: number;
    valueXs?: number;
    sx?: any;
};

function LabelRow({
    label,
    children,
    required,
    labelXs = 4,
    valueXs = 8,
    sx,
}: LabelRowProps) {
    return (
        <>
            <Grid item xs={labelXs} sx={sx}>
                <StyledInputLabel required={required}>{label}</StyledInputLabel>
            </Grid>
            <Grid item xs={valueXs} sx={sx}>
                {typeof children === 'string' ||
                typeof children === 'number' ? (
                    <Typography sx={{ color: 'text.primary' }}>
                        {children}
                    </Typography>
                ) : (
                    children
                )}
            </Grid>
        </>
    );
}

type Props = {
    isMobile: boolean;
    isIpad: boolean;
    theme: any;
    reservationDetails: any;
    loggedInUser: any;
    getIndividualAsset: any;
    createdAtDate: string;
    dateDisplay: string;
    onClickOpenModal: () => void;
    control: any;
    checkinTime: string;
    checkoutTime: string;
    compareCheckinSettings: any[];
    compareCheckoutSettings: any[];
    /** Approved reservation in progress: lock dates/times in header (attendees edited in body). */
    attendeesOnlyEditMode?: boolean;
};

export default function ReservationHeader({
    isMobile,
    isIpad,
    theme,
    reservationDetails,
    loggedInUser,
    getIndividualAsset,
    createdAtDate,
    dateDisplay,
    onClickOpenModal,
    control,
    checkinTime,
    checkoutTime,
    compareCheckinSettings,
    compareCheckoutSettings,
    attendeesOnlyEditMode = false,
}: Props) {
    const editable =
        !attendeesOnlyEditMode &&
        canEditReservationFields(reservationDetails, loggedInUser);
    const isOwner = loggedInUser?.role?.includes('owner');

    const checkinLabel =
        getIndividualAsset?.type === 'commercial' ? 'Access From:' : 'Checkin:';
    const checkoutLabel =
        getIndividualAsset?.type === 'commercial' ? 'Access To:' : 'Checkout:';

    const checkinValue = (() => {
        if (isOwner && reservationDetails?.onBehalf === false) return '-';
        if (getIndividualAsset?.type === 'commercial') return checkinTime;
        if (!editable) return checkinTime;

        return (
            <FormAutocomplete
                name='bookingInfo.checkinTime'
                control={control}
                disabled={!editable}
                options={compareCheckinSettings}
            />
        );
    })();

    const checkoutValue = (() => {
        if (isOwner && reservationDetails?.onBehalf === false) return '-';
        if (getIndividualAsset?.type === 'commercial')
            return getIndividualAsset?.details?.checkoutTime ?? checkoutTime;
        if (!editable) return checkoutTime;

        return (
            <FormAutocomplete
                name='bookingInfo.checkoutTime'
                control={control}
                disabled={!editable}
                options={compareCheckoutSettings}
            />
        );
    })();

    const dateValue = !editable ? (
        <Typography sx={{ color: 'text.primary' }}>{dateDisplay}</Typography>
    ) : (
        <CustomTextField
            disabled={!editable}
            fullWidth={isMobile}
            value={dateDisplay}
            onClick={onClickOpenModal}
            color='dark'
            inputProps={{
                style: {
                    height: isMobile ? '10px' : undefined,
                    width: isMobile ? undefined : isIpad ? '190px' : '190px',
                    backgroundColor: !editable
                        ? theme.palette.neutral.main
                        : 'white',
                },
            }}
        />
    );

    const assetImageSrc = getIndividualAsset?.photos?.[0]?.src;
    const isModuleAvailable = reservationDetails?.module?.isAvailable;
    const experienceName =
        reservationDetails?.module?.name || getIndividualAsset?.name || '';

    if (isMobile) {
        return (
            <Grid container rowSpacing={1}>
                <LabelRow label='Date:'>{dateValue}</LabelRow>
                <LabelRow label={checkinLabel}>{checkinValue}</LabelRow>
                <LabelRow label={checkoutLabel}>{checkoutValue}</LabelRow>
                <LabelRow label='Type:'>
                    {capitalizeName(reservationDetails?.module?.type)}
                </LabelRow>
                <LabelRow label='Status:'>
                    {capitalizeName(reservationDetails?.status)}
                </LabelRow>
            </Grid>
        );
    }

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                    mb: 2,
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant='h1'
                        sx={{
                            textAlign: 'left',
                            textTransform: 'uppercase',
                            fontSize: {
                                xs: '20px',
                                sm: '22px',
                                md: '24px',
                            },
                            lineHeight: 1.2,
                            m: 0,
                            pr: 1,
                        }}
                    >
                        {experienceName}
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            flexWrap: 'wrap',
                            mt: 0.75,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 12,
                                color: theme.palette.text.secondary,
                            }}
                        >
                            Reservation #{reservationDetails?.id}
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 12,
                                color: theme.palette.text.secondary,
                            }}
                        >
                            •
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 12,
                                color: theme.palette.text.secondary,
                            }}
                        >
                            Created {createdAtDate}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {assetImageSrc && isModuleAvailable ? (
                <Box
                    sx={{
                        mb: 1.5,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        backgroundColor: theme.palette.neutral.main,
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            height: {
                                xs: 128,
                                sm: 144,
                                md: 156,
                                lg: 168,
                            },
                            width: '100%',
                        }}
                    >
                        <Box
                            component='img'
                            src={assetImageSrc}
                            alt={
                                getIndividualAsset?.name ||
                                reservationDetails?.module?.name
                            }
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                            }}
                        />

                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                background:
                                    'linear-gradient(to top, rgba(0,0,0,0.45), rgba(0,0,0,0) 55%)',
                            }}
                        />

                        <Box
                            sx={{
                                position: 'absolute',
                                left: 16,
                                right: 16,
                                bottom: 12,
                                display: 'flex',
                                alignItems: 'flex-end',
                                justifyContent: 'space-between',
                                gap: 1.5,
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        color: '#fff',
                                        fontWeight: 900,
                                        fontSize: 14,
                                        lineHeight: 1.2,
                                        textShadow:
                                            '0 1px 2px rgba(0,0,0,0.35)',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {camelCaseToTitleCase(
                                        getIndividualAsset?.type,
                                    )}
                                </Typography>

                                {getIndividualAsset?.suburb ? (
                                    <Typography
                                        sx={{
                                            mt: 0.25,
                                            color: 'rgba(255,255,255,0.9)',
                                            fontWeight: 700,
                                            fontSize: 12,
                                            lineHeight: 1.2,
                                            textShadow:
                                                '0 1px 2px rgba(0,0,0,0.3)',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {getIndividualAsset?.suburb}
                                        {getIndividualAsset?.state
                                            ? `, ${getIndividualAsset?.state}`
                                            : ''}
                                    </Typography>
                                ) : null}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            ) : null}
        </Box>
    );
}
